import { StateCode, STATES } from '@shared/components/address-list/address-list-form.interface';

export interface SalesTaxReport {
  breakdown: {
    [key in keyof typeof STATES]: StateReport;
  };
  international_breakdown?: {
    [countryCode: string]: CountryReport;
  };
  summary: {
    date: {
      from: string;
      to: string;
    };
    deductions_and_exemptions: number;
    gross: number;
    market_place_sales_tax: number;
    market_place_sales: number;
    sales_subject_to_sales_tax: number;
    total_sales_tax: number;
    total_sales_tax_due: number;
  };
}

export interface StateReport {
  has_nexus: boolean;
  enrolled_in_autofilling: boolean;
  origin_vs_destination: string;
  sales_tax_shipping: boolean;

  payment_schedule: StateReportPaymentSchedule;

  reports: {
    breakdown?: {
      state_tax_total: number,
      city_tax_total: number,
      county_tax_total: number,
      special_tax_total: number,
      breakdown_detail: [
        {
          taxRegion: string,
          gross: number,
          sales_subject_to_sales_tax: number,
          total_sales_tax: number,
          state_taxable_amount: number,
          city_taxable_amount: number,
          county_taxable_amount: number,
          special_taxable_amount: number
        },
        {
          taxRegion: string,
          gross: number,
          sales_subject_to_sales_tax: number,
          total_sales_tax: number,
          state_taxable_amount: number,
          city_taxable_amount: number,
          county_taxable_amount: number,
          special_taxable_amount: number
        }
      ],
      breakdown_detail_compressed: [
        {
          taxRegion: string,
          gross: number,
          sales_subject_to_sales_tax: number,
          total_sales_tax: number,
          state_taxable_amount: number,
          city_taxable_amount: number,
          county_taxable_amount: number,
          special_taxable_amount: number
        },
        {
          taxRegion: string,
          gross: number,
          sales_subject_to_sales_tax: number,
          total_sales_tax: number,
          state_taxable_amount: number,
          city_taxable_amount: number,
          county_taxable_amount: number,
          special_taxable_amount: number
        }
      ],
    },
    gross: number;
    sales_subject_to_sales_tax: number;
    market_place_sales: number;
    deductions_and_exemptions: number;
    total_sales_tax: number;
    market_place_sales_tax: number;
    total_sales_tax_due: number;
  };
}

/** Country-level collected / expected report (international_breakdown). */
export interface CountryReport {
  has_nexus: boolean;
  enrolled_in_autofilling?: boolean;
  country_code: string;
  payment_schedule?: string;
  sales_tax_shipping?: boolean;
  expected?: boolean;
  reports: {
    gross?: number;
    sales_subject_to_sales_tax?: number;
    market_place_sales?: number;
    deductions_and_exemptions?: number;
    total_sales_tax?: number;
    market_place_sales_tax?: number;
    total_sales_tax_due?: number;
    country_tax?: number;
    breakdown_detail?: unknown[];
    provider_tax_breakdown?: unknown[];
  };
}

export interface StateReportPaymentSchedule {
  title: string;
  start: string;
  end: string;
  filingDate: string;
  state_abbr: string;
  payment_schedule: string;
}

export interface EconomicNexus {
  state_abbr: StateCode;
  country_code?: string;
  region_code?: string;
  tax_regime?: string;
  jurisdiction_type?: 'state' | 'country' | 'country_region';
  operation: 'OR' | 'AND';
  sales_threshold: number;
  transaction_threshold: number;
  current_transaction_amount: number;
  current_sales_amount: number;
  fiscal_bounds: Array<string>;
  fiscal_date: Array<string>;
  has_nexus_settings?: boolean;
  has_reached_nexus: boolean;
  sales_percent: number;
  transaction_percent: number;
}

export enum SalesReportRouteFragment {
  actual = 'actual',
  expected = 'expected',
  insights = 'insights',
}

/** Response from GET /sales-tax-report/detailed-analysis (merchant analytics; distinct from SalesTaxReport). */
export interface DetailedSalesTaxAnalysis {
  resource: 'detailed_sales_tax_analysis';
  generated_at: string;
  period: {
    from: string;
    to: string;
  } | null;
  totals: {
    gross: number;
    sales_subject_to_sales_tax: number;
    deductions_and_exemptions: number;
    total_sales_tax: number;
    total_sales_tax_due: number;
    marketplace_sales: number;
    marketplace_sales_tax: number;
  };
  composition: {
    sum_jurisdiction_state_tax: number;
    sum_jurisdiction_city_tax: number;
    sum_jurisdiction_county_tax: number;
    sum_jurisdiction_special_tax: number;
  };
  international_totals: {
    gross: number;
    sales_subject_to_sales_tax: number;
    total_sales_tax: number;
    total_sales_tax_due: number;
    marketplace_sales: number;
    marketplace_sales_tax: number;
  };
  states_ranked: DetailedSalesTaxAnalysisStateRow[];
  countries_ranked: DetailedSalesTaxAnalysisCountryRow[];
  insights: {
    states_in_scope: number;
    states_with_nexus: number;
    states_enrolled_autofiling: number;
    top_state_by_tax_due: string | null;
    countries_in_scope: number;
    countries_with_nexus: number;
    countries_enrolled_autofiling: number;
    top_country_by_tax_due: string | null;
    taxable_to_gross_ratio: number | null;
    international_taxable_to_gross_ratio: number | null;
  };
}

export interface DetailedSalesTaxAnalysisStateRow {
  state_abbr: string;
  state_name: string;
  has_nexus: boolean;
  enrolled_in_autofilling: boolean;
  origin_vs_destination: string | null;
  sales_tax_shipping: boolean | null;
  gross: number;
  sales_subject_to_sales_tax: number;
  total_sales_tax: number;
  total_sales_tax_due: number;
  marketplace_sales: number;
  marketplace_sales_tax: number;
  jurisdiction_breakdown_rows: number;
  tax_components: {
    state: number;
    city: number;
    county: number;
    special: number;
  };
  filing_period: {
    start: string;
    end: string;
    filing_date: string;
    payment_schedule: string;
    title: string;
  } | null;
}

export interface DetailedSalesTaxAnalysisCountryRow {
  breakdown_key?: string;
  country_code: string;
  region_code?: string | null;
  country_name: string;
  jurisdiction_label?: string;
  has_nexus: boolean;
  enrolled_in_autofilling: boolean;
  payment_schedule: string | null;
  gross: number;
  sales_subject_to_sales_tax: number;
  total_sales_tax: number;
  total_sales_tax_due: number;
  marketplace_sales: number;
  marketplace_sales_tax: number;
  provider_breakdown_rows: number;
}

export interface StateInsightsReport {
  insights: {
    over_collected_transactions: InsightsTransaction;
    under_collected_transactions: InsightsTransaction;
    no_sales_tax_collected: InsightsTransaction;
    penny_variance: InsightsTransaction;
  };
  summary: {
    filing_period: string;
    total_transactions: number;
    variance_transactions: number;
    net_variance_amount: number;
  };
}

interface InsightsTransaction {
  transaction_ids: string[];
  value: number;
}
